import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { Choice, MIN_VIEWS, ON_WINNER, ThankYouTestSetup } from "./ThankYouTestSetup";
import { ThankYouTestStandings } from "./ThankYouTestStandings";
import { ThankYouTestPolicy } from "./ThankYouTestPolicy";
import { ThankYouTestHistory } from "./ThankYouTestHistory";

const LIVE = ["running", "pending"];

function statusText(t, buyer, own) {
  const where = own ? "on your pages" : buyer ? "on this buyer's pages" : t.scope === "all" ? "on every page" : "on pages using the default design";
  const target = own ? "your thank-you page" : buyer ? "this buyer's thank-you page" : "your default";
  const decided = t.decided_by === "automatic" ? " and was switched on automatically" : ` (approved by ${t.decided_by})`;
  return {
    running: `Test running ${where}. ${t.tied ? "Every design has enough views but they're tied, so the test keeps going until one leads. " : ""}${t.on_winner === "approve" ? "You'll be asked to approve the winner." : `The winner becomes ${target} automatically.`}`,
    pending: `${t.winner_name} has the best tap rate. Approve to make it ${target}.`,
    finished: `${t.winner_name} won${decided}. It's now ${target}${!buyer && t.scope === "all" ? " and shows on every page" : ""}.`,
  }[t.status];
}

function LastTest({ t, p }) {
  const when = t.ended_at ? new Date(t.ended_at).toLocaleDateString() : "";
  if (t.status === "stopped") return <p className="text-[12px] text-zinc-500" data-testid={`${p}-last`}>Last test was stopped {when} — nothing was switched.</p>;
  if (t.status === "ended") {
    return <p className="text-[12px] text-zinc-500" data-testid={`${p}-last`}>Last test: {t.winner_name} won and is still in use. The test was closed {when}.</p>;
  }
  return null;
}

function Actions({ t, buyer, busy, act, p, onNew }) {
  const live = LIVE.includes(t.status);
  return (
    <div className="flex flex-wrap gap-2">
      {t.status === "pending" && <button type="button" className={BTN_BLACK} disabled={busy} data-testid={`${p}-approve`} onClick={() => act("post", "/approve", "Winner switched on")}>Approve the switch</button>}
      {live && <button type="button" className={BTN_WHITE} disabled={busy} data-testid={`${p}-stop`} onClick={() => act("post", "/stop", "Test stopped — nothing was switched")}>Stop test</button>}
      {t.status === "finished" && !buyer && t.scope === "all" && (
        <button type="button" className={BTN_WHITE} disabled={busy} data-testid={`${p}-end`} onClick={() => act("post", "/stop", "Pages are back on their own designs")}>Let pages use their own designs again</button>
      )}
      {t.status === "finished" && <button type="button" className={BTN_WHITE} disabled={busy} onClick={onNew} data-testid={`${p}-new`}>Start a new test</button>}
    </div>
  );
}

// base: "/admin/thankyou-test" (overall) or "/thankyou-tests/page/<id>" (one buyer). own: the buyer is looking at their own pages.
export function ThankYouTestPanel({ base = "/admin/thankyou-test", buyer = false, own = false, p = "ty-test" }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(false);
  const [ver, setVer] = useState(0);
  const act = useCallback(async (method, path = "", msg, body) => {
    setBusy(true);
    try {
      const r = await api[method](`${base}${path}`, body);
      setT(r.data);
      setSetup(false);
      setVer((v) => v + 1);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errText(e, "Couldn't update the test"));
    } finally {
      setBusy(false);
    }
  }, [base]);
  useEffect(() => { act("get"); }, [act]);
  if (!t) return null;
  const showSetup = setup || !(LIVE.includes(t.status) || t.status === "finished");
  const blocked = buyer && t.overlap === "block" && t.overall_running;
  return (
    <section className="mb-5 space-y-3 border-2 border-black p-3" data-testid={`${p}-panel`}>
      <div className="font-chivo font-black">{own ? "Thank-you design test for your pages" : buyer ? "Thank-you design test for these pages" : "Design test — pick the winner automatically"}</div>
      {buyer && t.overall_running && t.overlap !== "block" && <p className="text-[12px] text-zinc-600" data-testid={`${p}-priority-note`}>The overall test is running too — {own ? "your own test takes priority on your pages" : "this buyer's own test takes priority on their pages"}.</p>}
      {showSetup ? (
        <>
          <LastTest t={t} p={p} />
          {blocked
            ? <p className="border-2 border-amber-600 bg-amber-50 p-2 text-sm" data-testid={`${p}-blocked`}>The overall thank-you test is running. A test for {own ? "your" : "these"} pages can start once it finishes or is stopped.</p>
            : <ThankYouTestSetup available={t.available} busy={busy} buyer={buyer} own={own} p={p} onStart={(body) => act("post", "", "Test started", body)} />}
          {setup && <button type="button" className={BTN_WHITE} onClick={() => setSetup(false)} data-testid={`${p}-back`}>Back to the last result</button>}
        </>
      ) : (
        <>
          <p className={`border-2 border-black p-2 text-sm ${t.status === "running" ? "bg-white" : "bg-emerald-50"}`} data-testid={`${p}-status`}>{statusText(t, buyer, own)}</p>
          <ThankYouTestStandings rows={t.standings} minViews={t.min_views} winner={t.status === "running" ? null : t.winner} p={p} />
          {t.status === "running" && (
            <div className="grid gap-3 md:grid-cols-2">
              <Choice label="Pick a winner after" value={t.min_views} options={MIN_VIEWS} disabled={busy} testId={`${p}-edit-min-views`}
                onChange={(v) => act("patch", "", "Saved", { min_views: v })} />
              <Choice label="When a winner is found" value={t.on_winner} options={ON_WINNER} disabled={busy} testId={`${p}-edit-on-winner`}
                onChange={(v) => act("patch", "", "Saved", { on_winner: v })} />
            </div>
          )}
          <Actions t={t} buyer={buyer} busy={busy} act={act} p={p} onNew={() => setSetup(true)} />
        </>
      )}
      <ThankYouTestHistory url={buyer ? `${base}/history` : "/admin/thankyou-test-history"} version={ver} admin={!buyer} p={p} />
      {!buyer && <ThankYouTestPolicy overlap={t.overlap} onSaved={(overlap) => setT((x) => ({ ...x, overlap }))} />}
    </section>
  );
}
